import dynamic from 'next/dynamic';
import React, { useState } from 'react';
import { LoadingBar } from 'vcc-ui';

import { useUserContext } from '@collab/features/user';

import Accordion from 'organisms/Accordion';

import { useSwagger } from './SwaggerProvider';
import TryItOutProvider, { useTryItOut } from './impl/TryItOutProvider';
import usePostVolvoidLogin from './impl/usePostVolvoidLogin';

const AuthorizeSwagger = dynamic(() => import('./impl/AuthorizeSwagger'));
const LoginView = dynamic(() => import('./impl/LoginView'));

const TryItOut: React.FC<{
  apiId: string;
  apiTitle: string;
}> = ({ apiId, apiTitle }) => (
  <TryItOutProvider>
    <TryItOutAccordion apiId={apiId} apiTitle={apiTitle} />
  </TryItOutProvider>
);

const TryItOutAccordion: React.FC<{
  apiId: string;
  apiTitle: string;
}> = ({ apiId, apiTitle }) => {
  const [isOpen, setOpen] = useState(false);
  const { setAccountType } = useTryItOut();

  const onVolvoIdLogin = () => {
    setAccountType('volvoid');
    setOpen(true);
  };

  const { isLoading } = usePostVolvoidLogin(apiId, onVolvoIdLogin);

  return (
    <Accordion
      summaryAriaLabel="Authorise the specification to test the API"
      onChange={() => setOpen(!isOpen)}
      expanded={isOpen}
      summary={<Summary apiTitle={apiTitle} />}
    >
      {isOpen && <Details apiId={apiId} isLoading={isLoading} />}
    </Accordion>
  );
};

const Details: React.FC<{
  apiId: string;
  isLoading: boolean;
}> = ({ apiId, isLoading }) => {
  const { isLoggedIn, isUserStatusLoading } = useUserContext();
  const { isLoading: swaggerIsLoading } = useSwagger();

  let component;
  let minHeight = 40;

  if (isLoading || swaggerIsLoading || isUserStatusLoading) {
    component = <LoadingBar isLoading />;
  } else if (!isLoggedIn) {
    component = <LoginView />;
  } else {
    minHeight = 190;
    component = <AuthorizeSwagger apiId={apiId} />;
  }
  return (
    <div className="bg-secondary p-24" style={{ minHeight }}>
      {component}
    </div>
  );
};

const Summary: React.FC<{ apiTitle: string }> = ({ apiTitle }) => (
  <>
    <p className="font-medium">Try it out</p>
    <small className="micro text-secondary">
      {`Experiment with the ${apiTitle} using your
    own VCC API key and an existing account.`}
    </small>
  </>
);

export default TryItOut;
